import React, { PureComponent } from 'react';
import { FlexTabs } from 'app/components/common/FlexTabs';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { MonitoredEventsFilters } from 'app/components/dashboard/global/events/MonitoredEventFilters';
import {
    ExtendedMonitoredTv,
    MonitoredEvent,
    MonitoredMeasure,
    NoteEvent,
    OpsInstruction,
    WeatherEvent,
} from 'app/model/SOL49';
import { MonitoredEventsSettings } from 'app/components/dashboard/global/events/MonitoredEventsSettings';
import { TvRefLocation } from 'app/model/CORE';
import { WeatherEventPanel } from 'app/components/panels/predict/weather/WeatherEventPanel';
import { NotePanel } from 'app/components/panels/predict/notes/NotePanel';
import { OpsInstructionsPanel } from 'app/components/panels/predict/opsInstrusctions/OpsInstructionsPanel';
import memoizeOne from 'memoize-one';
import { ModalPanelProps } from 'app/components/panels/DashboardModal';
import { WeatherEventForm } from 'app/components/panels/predict/weather/WeatherEventForm';

export interface MonitoredEventPanelWrapperProps extends ModalPanelProps {
    monitoredEvents: MonitoredEvent[];
    defaultType: MonitoredEventType;
    selectedEvent?: string;
    settings?: MonitoredEventsSettings;
    onSettingsChange?: (settings: MonitoredEventsSettings) => void;
    filters?: MonitoredEventsFilters;
    onFiltersChange?: (filters: MonitoredEventsFilters) => void;
    measures: MonitoredMeasure[];
    defaultMeasures?: string[];
    tvs: ExtendedMonitoredTv[];
    defaultTv?: string;
    fmps: Set<string>;
    defaultFmp?: string[];
    refLocs: TvRefLocation[];
    defaultRefLoc?: TvRefLocation;
}

export interface MonitoredEventPanelWrapperState {
    selectedType: MonitoredEventType;
    settings: MonitoredEventsSettings;
    filters: MonitoredEventsFilters;
}

export class MonitoredEventPanelWrapper extends PureComponent<
    MonitoredEventPanelWrapperProps,
    MonitoredEventPanelWrapperState
> {
    public static WIDTH = '140em';

    constructor(props) {
        super(props);
        this.state = {
            selectedType: this.props.defaultType,
            settings: this.props.settings,
            filters: this.props.filters,
        };
    }

    render = () => (
        <FlexTabs
            centered
            style={{ padding: '2em' }}
            activeKey={this.state.selectedType}
            type="card"
            onTabClick={key => {
                if (this.state.selectedType !== key) {
                    // Update the panel width accordingly because the WeatherEvent tab has a very large width
                    if (key === MonitoredEventType.WEATHER_EVENT) {
                        this.props.onWidthChange(WeatherEventForm.getWidth());
                    } else if (this.state.selectedType === MonitoredEventType.WEATHER_EVENT) {
                        this.props.onWidthChange(MonitoredEventPanelWrapper.WIDTH);
                    }
                    this.setState({ selectedType: key as MonitoredEventType });
                }
            }}
            items={this.getPanels()}
        />
    );

    private getPanels = () => {
        const events = this.props.monitoredEvents;
        const opsInstructions = this.getOpsInstructions(events);
        const notes = this.getNotes(events);
        const weatherEvents = this.getWeatherEvents(events);
        return [
            {
                key: MonitoredEventType.OPS_INSTRUCTION,
                label: 'OPS INSTRUCTIONS',
                children: this.renderOpsInstructions(opsInstructions),
            },
            {
                key: MonitoredEventType.NOTE,
                label: 'NOTES',
                children: this.renderNotes(notes),
            },
            {
                key: MonitoredEventType.WEATHER_EVENT,
                label: 'EVENTS & WEATHER',
                children: this.renderEventsAndWeather(weatherEvents, opsInstructions),
            },
        ];
    };

    renderEventsAndWeather = (weatherEvents: WeatherEvent[], opsInstructions: OpsInstruction[]) => (
        <WeatherEventPanel
            events={weatherEvents}
            defaultEvents={weatherEvents
                .filter(e => e.refLocs?.map(refLoc => refLoc.refId).includes(this.props.defaultRefLoc?.refId))
                .map(e => e.id)}
            selected={this.props.selectedEvent}
            refLocs={this.props.refLocs}
            defaultRefLoc={this.props.defaultRefLoc}
            opsInstructions={opsInstructions}
            filters={this.state.filters}
            onFiltersChange={this.onFilter}
            settings={this.state.settings}
            onSettingsChange={this.onSettings}
            onWidthChange={this.props.onWidthChange}
        />
    );

    renderNotes = (notes: NoteEvent[]) => (
        <NotePanel
            events={notes}
            defaultEvents={notes
                .filter(
                    e =>
                        e.tvs.includes(this.props.defaultTv) ||
                        this.props.defaultMeasures?.some(m => e.measures.includes(m)),
                )
                .map(e => e.id)}
            selected={this.props.selectedEvent}
            tvs={this.props.tvs}
            tv={this.props.defaultTv}
            fmps={this.props.fmps}
            defaultFmp={this.props.defaultFmp}
            measures={this.props.measures}
            defaultMeasures={this.props.defaultMeasures}
            settings={this.state.settings}
            onSettingsChange={this.onSettings}
        />
    );

    renderOpsInstructions = (opsInstructions: OpsInstruction[]) => (
        <OpsInstructionsPanel
            events={opsInstructions}
            selected={this.props.selectedEvent}
            settings={this.state.settings}
            onSettingsChange={this.onSettings}
        />
    );

    private getOpsInstructions = memoizeOne((events: MonitoredEvent[]) =>
        events.filter(MonitoredEventUtils.isOpsInstruction),
    );

    private getNotes = memoizeOne((events: MonitoredEvent[]) => events.filter(MonitoredEventUtils.isNoteEvent));

    private getWeatherEvents = memoizeOne((events: MonitoredEvent[]) =>
        events.filter(MonitoredEventUtils.isWeatherEvent),
    );

    private onSettings = (settings: MonitoredEventsSettings) =>
        this.setState({ settings }, () => this.props.onSettingsChange?.(this.state.settings));

    private onFilter = (filters: MonitoredEventsFilters) =>
        this.setState({ filters }, () => this.props.onFiltersChange?.(this.state.filters));
}
